// Pure-function status engine for the Guest Communications Tracker.
// No React, no DB calls — easy to unit-test, easy to call from anywhere.

import { cleanPhone } from './utils.js';

export const CIRCLE_KEYS = [
  'confirmation',
  'deposit_request',
  'balance_reminder',
  'arrival_reminder',
  'review_request',
];

const RECENCY_DAYS_PAST = 14;    // hide circles for bookings whose check-out is older than this
const RECENCY_DAYS_FUTURE = 365; // hide circles for bookings whose check-in is further than this
                                 // (long enough to cover next-season bookings — owners want to be
                                 // able to send confirmation messages immediately after creating
                                 // a booking, even when the stay is months away)

const TIMING = {
  // anchor: 'createdAt' | 'checkIn' | 'checkOut'
  // dueOffsetDays / lateOffsetDays: number of days from the anchor (negative = before)
  confirmation:     { anchor: 'createdAt', dueOffsetDays: 0,  lateOffsetDays: 1 },
  deposit_request:  { anchor: 'createdAt', dueOffsetDays: 0,  lateOffsetDays: 2 },
  balance_reminder: { anchor: 'checkIn',   dueOffsetDays: -3, lateOffsetDays: -1 },
  arrival_reminder: { anchor: 'checkIn',   dueOffsetDays: -1, lateOffsetDays: 0 },
  review_request:   { anchor: 'checkOut',  dueOffsetDays: 1,  lateOffsetDays: 3 },
};

function isoToDate(iso) {
  // Treat ISO date (YYYY-MM-DD) as midnight UTC for stable day-math.
  return new Date(iso.length === 10 ? iso + 'T00:00:00Z' : iso);
}

function daysBetween(aIso, bIso) {
  const a = isoToDate(aIso).getTime();
  const b = isoToDate(bIso).getTime();
  return Math.round((a - b) / 86400000);
}

function addDays(iso, n) {
  const d = isoToDate(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

function inRecencyWindow(booking, todayIso) {
  const futureGap = daysBetween(booking.checkIn, todayIso);   // positive when check-in is after today
  const pastGap   = daysBetween(todayIso, booking.checkOut);  // positive when today is after check-out
  return futureGap <= RECENCY_DAYS_FUTURE && pastGap <= RECENCY_DAYS_PAST;
}

function anchorIso(booking, anchor) {
  if (anchor === 'createdAt') return (booking.createdAt || '').slice(0, 10);
  if (anchor === 'checkIn')   return booking.checkIn;
  return booking.checkOut;
}

function isTrustedGuest(booking, guestTrusted) {
  if (!guestTrusted) return false;
  const phoneKey = cleanPhone(booking.phone);
  return phoneKey ? !!guestTrusted[phoneKey] : false;
}

function isLocked(booking, key, property) {
  const hasPhone = !!(booking.phone && booking.phone.trim());
  const hasEmail = !!(booking.email && booking.email.trim());
  if (!hasPhone && !hasEmail) return true;

  if (key === 'deposit_request') {
    if (booking.source === 'Booking.com') return true;
    if (booking.paymentStatus !== 'Unpaid') return true;
    const hasBank = !!(property?.bank_iban && property?.bank_name && property?.bank_account_holder);
    const hasStripe = !!property?.stripe_restricted_key;
    if (!hasBank && !hasStripe) return true;
  }
  if (key === 'balance_reminder') {
    if (booking.paymentStatus !== 'Deposit') return true;
  }
  if (key === 'review_request') {
    if (!property?.google_review_link) return true;
  }
  return false;
}

export function circleStatus(booking, key, property, todayIso, guestTrusted) {
  if (!property?.guest_comm_enabled) return 'hidden';
  if (booking.status === 'cancelled') return 'hidden';
  if (!inRecencyWindow(booking, todayIso)) return 'hidden';
  if (property?.message_template_enabled?.[key] === false) return 'hidden';
  // Booking.com bookings: confirmation/payment/arrival are all handled by Booking.com
  // directly. Only the post-stay review_request makes sense for the host to send —
  // it nudges the guest to leave a review AND to book direct next time.
  if (booking.source === 'Booking.com' && key !== 'review_request') return 'hidden';
  // Once a booking is fully Paid, the balance reminder is irrelevant — hide it
  // even if a reminder was previously sent (the audit trail lives in messageLog).
  if (key === 'balance_reminder' && booking.paymentStatus === 'Paid') return 'hidden';

  const timing = TIMING[key];
  const anchor = anchorIso(booking, timing.anchor);
  const lateThreshold = addDays(anchor, timing.lateOffsetDays);
  const dueThreshold  = addDays(anchor, timing.dueOffsetDays);

  // Compressed-timeline rule: created_at after the late threshold → never had a chance.
  // Skip this rule when the anchor IS createdAt (those circles are due-on-creation by definition).
  // Also skip for balance_reminder if deposit was already received (paymentStatus === 'Deposit')
  // since that proves communication happened.
  const createdIso = (booking.createdAt || '').slice(0, 10);
  const isBalanceWithDeposit = key === 'balance_reminder' && booking.paymentStatus === 'Deposit';
  if (createdIso && timing.anchor !== 'createdAt' && !isBalanceWithDeposit && daysBetween(createdIso, lateThreshold) >= 0) {
    return 'hidden';
  }

  // Fresh-start rule: the created-at-anchored reminders (confirmation,
  // deposit_request) for bookings made BEFORE messaging was switched on are
  // historical backlog, not "going forward" — hide them so enabling the panel
  // never dumps a pile of overdue confirmations to send. Stay-anchored reminders
  // are unaffected (they only fire near the stay). Absent timestamp = no-op.
  const commEnabledIso = (property.guest_comm_enabled_at || '').slice(0, 10);
  if (commEnabledIso && timing.anchor === 'createdAt' && createdIso && createdIso < commEnabledIso) {
    return 'hidden';
  }

  const log = booking.messageLog || {};
  if (log[key + '_sent_at']) return 'sent';
  if (log[key + '_skipped_at']) return 'skipped';

  // Trusted-guest rule: deposit_request and balance_reminder are auto-hidden
  // for guests flagged as trusted via guest_notes.trusted. Looked up by phone_key.
  if ((key === 'deposit_request' || key === 'balance_reminder') && isTrustedGuest(booking, guestTrusted)) {
    return 'hidden';
  }

  if (isLocked(booking, key, property)) return 'locked';

  if (daysBetween(todayIso, lateThreshold) >= 0) return 'late';
  if (daysBetween(todayIso, dueThreshold) >= 0)  return 'due';
  // Booking.com bookings: hide review_request until it's actually due. It's the only
  // circle that ever surfaces for these bookings, so a lone faded "soon" circle would
  // make the panel appear with nothing to do — owners want it to show only when there's
  // a review to send (1 day after check-out).
  if (booking.source === 'Booking.com' && key === 'review_request') return 'hidden';
  return 'soon';
}

export function relevantCircles(booking, property, todayIso, guestTrusted) {
  return CIRCLE_KEYS.map(key => ({ key, status: circleStatus(booking, key, property, todayIso, guestTrusted) }));
}

const STATUS_RANK = { late: 0, due: 1 };
const FEED_CAP = 10;

function gatherTodayActions(bookings, property, todayIso, guestTrusted) {
  const rows = [];
  for (const booking of bookings) {
    for (const key of CIRCLE_KEYS) {
      const status = circleStatus(booking, key, property, todayIso, guestTrusted);
      if (status !== 'late' && status !== 'due') continue;
      const anchor = anchorIso(booking, TIMING[key].anchor);
      rows.push({ bookingId: booking.id, key, status, anchor, booking });
    }
  }

  // Dedupe by (phone-or-email, key, anchor). Keep the first occurrence.
  const seen = new Set();
  const deduped = [];
  for (const row of rows) {
    const contact = row.booking.phone || row.booking.email || row.booking.id;
    const fingerprint = contact + '|' + row.key + '|' + row.anchor;
    if (seen.has(fingerprint)) continue;
    seen.add(fingerprint);
    deduped.push(row);
  }

  deduped.sort((a, b) => {
    const r = STATUS_RANK[a.status] - STATUS_RANK[b.status];
    if (r !== 0) return r;
    return a.anchor.localeCompare(b.anchor);
  });

  return deduped;
}

export function buildTodayFeed(bookings, property, todayIso, guestTrusted) {
  return gatherTodayActions(bookings, property, todayIso, guestTrusted).slice(0, FEED_CAP);
}

export function countTodayActions(bookings, property, todayIso, guestTrusted) {
  return gatherTodayActions(bookings, property, todayIso, guestTrusted).length;
}
